import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { AiFillLock } from 'react-icons/ai';
import { FaRegClock } from 'react-icons/fa';
import { HiChevronUp, HiChevronDown } from 'react-icons/hi';
import { useSelector } from 'react-redux';

import { SchoolConfigData } from '@core/blocks/edu-match/components/ExitStrategy/type';
import useCheckScreen from '@core/blocks/eduResults/hooks/useCheckScreen';
import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import { useNoPiiVariant } from '@core/hooks/useNoPiiVariant';
import { selectDcsDegrees } from '@core/reducers/dcsSlice';
import { selectTestResultById } from '@core/reducers/monarchSlice';
import LearnMoreHeaderAndSubheader from '@core/shared/components/LearnMoreHeaderAndSubheader';
import PulsingDotLoader from '@core/shared/components/PulsingDotLoader';
import SimpleCollapsible from '@core/shared/components/SimpleCollapsible';
import TcpaSection from '@core/shared/features/TcpaSection';
import { VoyagerResult } from '@core/ts/results';
import cn from '@core/utils/classNames';
import { isUnderGraduate } from '@core/utils/isUnderGraduate';

import AccordionItem from '../AccordionItem';
import ConfirmationRevamp from '../ConfirmationRevamp';
import NoPiiVariantConfirmation from './NoPiiVariantConfirmation';
import styles from './PreSubmissionPageRevamp.module.css';

type Props = {
  result: VoyagerResult;
  schoolConfigData: SchoolConfigData;
  handleSubmit: () => void;
  setFirstName: Dispatch<SetStateAction<string>>;
  setLastName: Dispatch<SetStateAction<string>>;
  setEmail: Dispatch<SetStateAction<string>>;
  setPhone: Dispatch<SetStateAction<string>>;
  piiLeadErrors: { field: string }[];
  isHeclidReady: boolean;
  isSubmitting: boolean;
};

const PreSubmissionPageRevamp: FC<Props> = ({
  result,
  schoolConfigData,
  handleSubmit,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  piiLeadErrors,
  isHeclidReady,
  isSubmitting,
}) => {
  const flags = useFeatureFlags();
  const { school, program } = result;
  const tcpa = schoolConfigData ? { ...schoolConfigData, providerName: program?.providerName, hit: result } : null;
  const { isMobile } = useCheckScreen();
  const [degree] = useSelector(selectDcsDegrees);
  const { variant, isNoPiiTestEnabled } = useNoPiiVariant();

  const isUndergrad = isUnderGraduate(degree);

  const isMobileUndergrad = isMobile && isUndergrad;

  const isUGToGradResultsPageABTest = flags?.voyagerGradResultsPage === 'test';
  const isConversionCtaTest = flags?.conversionCtaCopyUnderGrad === 'test';
  const ctaLabel =
    isConversionCtaTest || isUGToGradResultsPageABTest || isMobileUndergrad ? 'Learn More' : 'Confirm & Submit';
  const buttonText = isSubmitting || !isHeclidReady ? <PulsingDotLoader /> : ctaLabel;

  const noPii = useSelector((state) => selectTestResultById(state, 'noPii'));

  const isNoPiiVariant = isNoPiiTestEnabled && (variant === 'noPiiZipOnly' || variant === 'noPiiZipName');

  const heading = noPii ? (
    <span className={styles.securityMessage}>
      <AiFillLock className={styles.lockIcon} />
      We never share your info without your consent.
    </span>
  ) : (
    'Confirm your contact details:'
  );

  return (
    <>
      <div
        className={cn(
          styles.preSubContainer,
          isConversionCtaTest && styles.gradRpTestBg,
          isUGToGradResultsPageABTest && styles.ugToGradTest
        )}
      >
        <div className={styles.highlightsContainer}>
          {!isNoPiiVariant && !noPii && (isConversionCtaTest || isUGToGradResultsPageABTest) && (
            <LearnMoreHeaderAndSubheader />
          )}

          {!isUGToGradResultsPageABTest && (
            <>
              <span className={cn(styles.highlightsProgram, isConversionCtaTest && styles.gradRpTestProgramName)}>
                {program.name}
              </span>
              <h2 className={cn(styles.highlightsTitle, isConversionCtaTest && styles.gradRpTestHighlightTitle)}>
                School Highlights
              </h2>
              <div className={styles.flagsContainer}>
                {schoolConfigData.showNextStartDate && (
                  <p className={styles.acceptingStudentsCta}>
                    <FaRegClock size="20px" /> Now Accepting Students
                  </p>
                )}

                <ul className={styles.flagsList}>
                  <li>{school?.additional?.isPrivate ? <>Private</> : <>Public</>}</li>
                  <li>{school?.additional?.isOnline ? <>Online</> : <>Oncampus</>}</li>
                  <li>{school?.additional?.notForProfit ? <>Nonprofit</> : <>Forprofit</>}</li>
                </ul>
              </div>
              <div className={styles.highlightsContent}>
                <p className={styles.highlightsContentIntro}>{program.snippet}</p>
                <div className={styles.highlightsList}>
                  {!isUGToGradResultsPageABTest &&
                    schoolConfigData.valueProps.map(
                      (highlight: { title: string; description: string }, index: number) => (
                        <AccordionItem
                          key={`${highlight.title}-${index}`}
                          title={highlight.title}
                          description={highlight.description}
                          index={index}
                        />
                      )
                    )}
                </div>
              </div>
            </>
          )}
        </div>
        {isNoPiiVariant ? (
          <NoPiiVariantConfirmation
            isHeclidReady={isHeclidReady}
            isSubmitting={isSubmitting}
            buttonText={buttonText}
            piiLeadErrors={piiLeadErrors}
            setFirstName={setFirstName}
            setLastName={setLastName}
            setEmail={setEmail}
            setPhone={setPhone}
            handleSubmit={handleSubmit}
            tcpa={tcpa}
            privacyNoteLockClassName={styles.variantLockIcon}
          />
        ) : (
          <form onSubmit={(e) => e.preventDefault()} data-tf-element-role="offer" className={styles.submissionSidebar}>
            <span className={cn(styles.highlightsProgramMobile, isConversionCtaTest && styles.gradRpTestProgramName)}>
              {program.name}
            </span>
            {noPii && (isConversionCtaTest || isUGToGradResultsPageABTest) && (
              <LearnMoreHeaderAndSubheader noPii={noPii} />
            )}
            <h3 className={styles.confirmDetailsTitle}>{heading}</h3>
            <ConfirmationRevamp
              setFirstName={setFirstName}
              setLastName={setLastName}
              setEmail={setEmail}
              setPhone={setPhone}
              piiLeadErrors={piiLeadErrors}
            />

            <div className={styles.disclaimers}>
              {!noPii && <h2>Your information will never be shared with a school without your consent.</h2>}
              <TcpaSection tcpa={tcpa ? [tcpa] : null} />
            </div>
            {!noPii ? (
              <>
                <button
                  className={styles.confirmationButton}
                  type="submit"
                  data-tf-element-role="submit"
                  disabled={!isHeclidReady || isSubmitting}
                  onClick={handleSubmit}
                >
                  {buttonText}
                </button>
                <div className={styles.mobileHighlightsContainer}>
                  {isUGToGradResultsPageABTest && <LearnMoreHeaderAndSubheader />}
                </div>
              </>
            ) : (
              <>
                <button
                  className={styles.noPiiConfirmationButton}
                  type="submit"
                  data-tf-element-role="submit"
                  disabled={!isHeclidReady || isSubmitting}
                  onClick={handleSubmit}
                >
                  {buttonText}
                </button>
                <div className={styles.mobileHighlightsContainer}>
                  {isUGToGradResultsPageABTest && <LearnMoreHeaderAndSubheader />}
                  <SimpleCollapsible title="Why we ask for your information">
                    <div>
                      <p>
                        Your phone number allows a school advisor to contact you with details about this program,
                        financial aid, and your application process. Your email allows the school to send you
                        information such as application deadlines and links to upload your transcripts.
                      </p>
                    </div>
                  </SimpleCollapsible>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </>
  );
};

export default PreSubmissionPageRevamp;
